import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import * as React from "react";

import { Button } from "@/shared/foundation/primitives";

import { type DatasetTriageGoalState } from "../buildRequestTemplate";

import styles from "./DatasetTriageGoalForm.module.css";
import { ImplicitDatasetChip } from "./ImplicitDatasetChip";
import {
  MAX_TRIAGE_LABELS,
  type TriageGoalErrors,
  type TriageRowError,
} from "./validation";

interface Props {
  goal: DatasetTriageGoalState;
  errors: TriageGoalErrors;
  onChange: (next: DatasetTriageGoalState) => void;
  /** When false, the row trash buttons, the "Add label" button, and the
   *  per-row expand toggle are hidden so the form reads as a read-only
   *  view. The inputs themselves are disabled by the surrounding
   *  ``<fieldset>`` in ``AgentEditor``; this prop is purely about
   *  removing edit-only affordances. */
  isEditing: boolean;
}

type RowPatch = { label?: string; description?: string };

function inputClass(hasError: boolean): string {
  return `${styles.input} ${hasError ? styles.inputError : ""}`;
}

function ErrorList({ errors }: { errors: TriageRowError[] }) {
  return (
    <>
      {errors.map((err) => (
        <div key={err.message} className={styles.error}>
          {err.message}
        </div>
      ))}
    </>
  );
}

/**
 * First-class form for ``DatasetTriageGoal``.
 *
 * The label vocabulary is the only operator-facing input. Each row pairs
 * a label (the value applied as a tag if the LLM picks it) with a
 * description (rendered into the LLM's deliberation prompt). Rows are
 * stored as an array rather than a dict so reordering is possible and
 * the React keys stay stable as the user edits a label name.
 */
export function DatasetTriageGoalForm({
  goal,
  errors,
  onChange,
  isEditing,
}: Props) {
  const goalLevelErrors = errors.rows.filter((e) => e.index === -1);

  // Per-row "expanded" state lives here rather than in each row so the
  // set survives a parent re-render that reorders the array (e.g. when
  // a row above is deleted). Keyed by array index — adequate for the
  // current "edit a vocabulary you authored" workflow; if drag-reorder
  // lands, switch to a row-identity key.
  const [expandedRows, setExpandedRows] = React.useState<Set<number>>(
    () => new Set(),
  );
  const toggleExpanded = (idx: number) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const updateRow = (idx: number, patch: RowPatch) => {
    onChange({
      ...goal,
      label_vocabulary: goal.label_vocabulary.map((row, i) =>
        i === idx ? { ...row, ...patch } : row,
      ),
    });
  };

  const removeRow = (idx: number) => {
    onChange({
      ...goal,
      label_vocabulary: goal.label_vocabulary.filter((_, i) => i !== idx),
    });
    // Discard expansion state for the removed row and shift any indices
    // above it down by one to keep the set aligned with the new array.
    setExpandedRows((prev) => {
      const next = new Set<number>();
      for (const expandedIdx of prev) {
        if (expandedIdx === idx) {
          continue;
        }
        next.add(expandedIdx > idx ? expandedIdx - 1 : expandedIdx);
      }
      return next;
    });
  };

  const addRow = () => {
    onChange({
      ...goal,
      label_vocabulary: [
        ...goal.label_vocabulary,
        { label: "", description: "" },
      ],
    });
  };

  const canAddMore = goal.label_vocabulary.length < MAX_TRIAGE_LABELS;

  return (
    <div className={styles.container}>
      <ImplicitDatasetChip />
      <div className={styles.field}>
        <label className={styles.label}>Label vocabulary</label>
        <p className={styles.help}>
          Each label is a tag the agent may apply to the dataset; the
          description tells the model what the label means. You can reference
          variables with <code>{"{{name}}"}</code> in descriptions.
        </p>
        {goal.label_vocabulary.length === 0 ? (
          <div className={styles.empty}>No labels yet. Add at least one.</div>
        ) : (
          <ul className={styles.rows}>
            {goal.label_vocabulary.map((row, idx) => (
              <LabelRow
                key={idx}
                idx={idx}
                row={row}
                rowErrors={errors.rows.filter((e) => e.index === idx)}
                isExpanded={expandedRows.has(idx)}
                isEditing={isEditing}
                onChange={(patch) => updateRow(idx, patch)}
                onRemove={() => removeRow(idx)}
                onToggleExpanded={() => toggleExpanded(idx)}
              />
            ))}
          </ul>
        )}
        {isEditing && (
          <div className={styles.addRow}>
            <Button size="small" onClick={addRow} disabled={!canAddMore}>
              Add label
            </Button>
            {/* Just the running count. ``MAX_TRIAGE_LABELS`` (50) is far
                above any realistic vocabulary, so the denominator would
                advertise a cap nobody hits. The ``disabled={!canAddMore}``
                above still enforces the limit silently. */}
            <span className={styles.countHint}>
              {goal.label_vocabulary.length}
            </span>
          </div>
        )}
        <ErrorList errors={goalLevelErrors} />
      </div>
    </div>
  );
}

interface LabelRowProps {
  idx: number;
  row: { label: string; description: string };
  rowErrors: TriageRowError[];
  /** When true, the row reveals a full-width textarea below the single-
   *  line description input. Toggle is purely UI state; both editors
   *  bind to the same ``row.description`` field. */
  isExpanded: boolean;
  /** Whether the surrounding AgentEditor is in edit mode. Toggles the
   *  expand and trash affordances on a per-row basis. */
  isEditing: boolean;
  onChange: (patch: RowPatch) => void;
  onRemove: () => void;
  onToggleExpanded: () => void;
}

function LabelRow({
  idx,
  row,
  rowErrors,
  isExpanded,
  isEditing,
  onChange,
  onRemove,
  onToggleExpanded,
}: LabelRowProps) {
  const labelErrors = rowErrors.filter((e) => e.field === "label");
  const descriptionErrors = rowErrors.filter((e) => e.field === "description");
  // 1-based for human-facing ARIA labels; `idx` is the 0-based array index.
  const humanIndex = idx + 1;
  const expandedTextareaId = `triage-description-${idx}`;
  return (
    <li className={styles.row}>
      <div className={styles.rowInputs}>
        <div className={styles.labelCell}>
          <input
            aria-label={`label ${humanIndex}`}
            className={inputClass(labelErrors.length > 0)}
            value={row.label}
            placeholder="anomalous"
            onChange={(e) => onChange({ label: e.target.value })}
          />
          <ErrorList errors={labelErrors} />
        </div>
        <div className={styles.descCell}>
          <input
            aria-label={`description ${humanIndex}`}
            className={inputClass(descriptionErrors.length > 0)}
            value={row.description}
            placeholder="Worth a human look — unusual signal patterns."
            onChange={(e) => onChange({ description: e.target.value })}
            // When the expanded textarea is on screen, hide the single-
            // line input from sequential focus and screen readers — it
            // would otherwise be a redundant second editor for the same
            // value. ``aria-hidden`` keeps the value visible as static
            // text while the textarea below is the active editor.
            aria-hidden={isExpanded}
            tabIndex={isExpanded ? -1 : undefined}
          />
          <ErrorList errors={descriptionErrors} />
        </div>
        <div className={styles.rowActions}>
          {/* The expand toggle is a view affordance — readers in read mode
              still need to be able to open the full-width editor to see a
              long description that would otherwise be clipped by the
              single-line input. The surrounding ``<fieldset disabled>``
              cascade in ``AgentEditor`` disables every descendant
              ``<button>`` (per HTML spec), so the toggle has to be a
              non-form-control element to survive read mode. ``role="button"``
              + ``tabIndex`` + Enter/Space keyboard handling restore the
              button affordance to AT and keyboard users. */}
          <span
            role="button"
            tabIndex={0}
            aria-label={
              isExpanded
                ? `collapse description editor for label ${humanIndex}`
                : `expand description editor for label ${humanIndex}`
            }
            aria-expanded={isExpanded}
            aria-controls={expandedTextareaId}
            className={styles.iconButton}
            onClick={onToggleExpanded}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                // Prevent ``Space`` from scrolling the page when the toggle
                // has focus; Enter has no scroll default but the early
                // preventDefault keeps the branch symmetric.
                e.preventDefault();
                onToggleExpanded();
              }
            }}
          >
            {isExpanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </span>
          {isEditing && (
            <button
              type="button"
              aria-label={`remove label ${humanIndex}`}
              className={styles.removeButton}
              onClick={onRemove}
            >
              <DeleteOutlineIcon fontSize="small" />
            </button>
          )}
        </div>
      </div>
      {isExpanded && (
        <textarea
          id={expandedTextareaId}
          aria-label={`description ${humanIndex} (full editor)`}
          className={`${styles.expandedDescription} ${
            descriptionErrors.length > 0 ? styles.inputError : ""
          }`}
          rows={4}
          value={row.description}
          placeholder="Worth a human look — unusual signal patterns. Reference variables with {{name}}."
          onChange={(e) => onChange({ description: e.target.value })}
        />
      )}
    </li>
  );
}
